import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const CreateBooking = () => {
    const navigate = useNavigate();
    const userType = getUserType();
    const allowEdit = canEdit(userType, "bookings");

    // Reference Data
    const [customers, setCustomers] = useState([]);
    const [units, setUnits] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    // Form State
    const [form, setForm] = useState({
        customer_id: "",
        customer_name: "",
        cust_phone: "",
        unit_id: "",
        unit: "",
        unit_id2: "",
        unit2: "",
        project_id: "",
        project_name: "",
        sale_type: "",
        plan_type: "",
        construction_type: "",
        buildarea: "",
        agreement_type: "",
        comment: "",
        sale_price: "",
        land_cost: "",
        payment_source: "",
        loan_amount: "",
    });

    const [applyGST, setApplyGST] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState({ text: "", type: "" });
    const [searchCustomer, setSearchCustomer] = useState("");
    const [searchUnit, setSearchUnit] = useState("");
    const [addSecondaryUnit, setAddSecondaryUnit] = useState(false);
    const [searchUnit2, setSearchUnit2] = useState("");

    useEffect(() => {
        if (!allowEdit) {
            navigate("/dashboard/bookings");
            return;
        }

        const fetchCustomers = fetch(`${API_BASE}/api/allrows`, {
            method: "POST", headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "customers" })
        }).then(r => r.json());

        const fetchUnits = fetch(`${API_BASE}/api/allrows`, {
            method: "POST", headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "units" })
        }).then(r => r.json());

        Promise.all([fetchCustomers, fetchUnits])
            .then(([custRes, unitsRes]) => {
                if (Array.isArray(custRes.data)) setCustomers(custRes.data);
                if (Array.isArray(unitsRes.data)) {
                    // Only map available units
                    setUnits(unitsRes.data.filter(u => u.status === "Available"));
                }
                setLoadingData(false);
            })
            .catch(() => setLoadingData(false));
    }, [navigate, allowEdit]);

    // Financial Calculations
    const sale_price = Number(form.sale_price) || 0;
    const land_cost = Number(form.land_cost) || 0;
    const construction_cost = Math.max(0, sale_price - land_cost);
    const sale_gst = applyGST ? Math.round(construction_cost * 0.06) : 0;
    const total_saleprice = sale_price + sale_gst;

    const loan_amount = Number(form.loan_amount) || 0;
    let self_pay = total_saleprice;
    if (form.payment_source === "Self + Bank Loan") {
        self_pay = Math.max(0, total_saleprice - loan_amount);
    }

    const handleCustomerSelect = (c) => {
        setForm({ ...form, customer_id: c.id, customer_name: c.name, cust_phone: c.phone });
        setSearchCustomer(`${c.name} (${c.phone})`);
    };

    const handleUnitSelect = (u) => {
        setForm({ ...form, unit_id: u.id, unit: u.name, project_id: u.project_id, project_name: u.project });
        setSearchUnit(u.name);
    };

    const handleUnitSelect2 = (u) => {
        setForm({ ...form, unit_id2: u.id, unit2: u.name });
        setSearchUnit2(u.name);
    };

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    // Handle cascading dropdown resets
    useEffect(() => {
        let newAgreement = form.agreement_type;
        if (form.sale_type === "Plot") newAgreement = "Plot";
        if (form.sale_type === "Shop") newAgreement = "Shop";
        if (["Singlex", "Duplex"].includes(form.sale_type) && !["Unit", "P+C"].includes(form.agreement_type)) newAgreement = "";

        setForm(prev => ({
            ...prev,
            agreement_type: newAgreement,
            plan_type: ["Singlex", "Duplex"].includes(prev.sale_type) ? prev.plan_type : "",
            construction_type: ["Singlex", "Duplex"].includes(prev.sale_type) && prev.plan_type ? prev.construction_type : "",
            buildarea: prev.plan_type === "Customized" ? prev.buildarea : "",
            loan_amount: prev.payment_source === "Self + Bank Loan" ? prev.loan_amount : ""
        }));
        // eslint-disable-next-line
    }, [form.sale_type, form.plan_type, form.payment_source]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.customer_id || !form.unit_id || !form.sale_type || !form.agreement_type || !form.sale_price) {
            setMsg({ text: "Please fill all required primary fields.", type: "error" });
            window.scrollTo(0, 0);
            return;
        }

        setSaving(true);
        setMsg({ text: "", type: "" });

        const formData = new FormData();
        formData.append('customer_name', form.customer_name);
        formData.append('customer_id', form.customer_id);
        formData.append('unit_name', form.unit);
        formData.append('unit_id', form.unit_id);
        formData.append('unit2', addSecondaryUnit ? form.unit2 : "");
        formData.append('unit_id2', addSecondaryUnit ? form.unit_id2 : "");
        formData.append('plan', form.plan_type);
        formData.append('construction_type', form.construction_type);
        formData.append('comment', form.comment);
        formData.append('agreement_type', form.agreement_type);
        formData.append('sale_type', form.sale_type);
        formData.append('buildarea', form.buildarea);
        formData.append('sale_price', form.sale_price);
        formData.append('land_cost', form.land_cost);
        formData.append('construction_cost', construction_cost);
        formData.append('total_saleprice', total_saleprice);
        formData.append('payment_source', form.payment_source);
        formData.append('loan_amount', form.loan_amount);
        formData.append('self_pay', self_pay);
        formData.append('sale_gst', sale_gst);

        // Required API credentials
        formData.append('token', '3232');

        fetch(`${API_BASE}/api/createbooking`, {
            method: "POST",
            headers: { 
                Authorization: "1",
                Table: "bookings"
            },
            body: formData
        })
            .then(res => res.json())
            .then(json => {
                setSaving(false);
                if (json.status === true || json.status === "true" || json.message === "Data inserted successfully") {
                    setMsg({ text: "Booking created successfully!", type: "success" });
                    // Optional: Update unit status to Booked
                    fetch(`${API_BASE}/api/updatedatas`, {
                        method: "POST",
                        headers: { Authorization: "1", Table: "units", Id: form.unit_id },
                        body: new URLSearchParams({ status: "Booked" })
                    }).catch(console.error);
                    setTimeout(() => navigate("/dashboard/bookings"), 1500);
                } else {
                    setMsg({ text: json.message || "Failed to create booking.", type: "error" });
                }
            })
            .catch(() => {
                setSaving(false);
                setMsg({ text: "Network error creating booking.", type: "error" });
            });
    };

    if (loadingData) return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Loading assets...</div>;

    const showPlan = ["Singlex", "Duplex"].includes(form.sale_type);
    const showCustom = form.plan_type === "Customized";

    return (
        <div className="cb-page">
            <style>{`
        .cb-page { max-width:900px; margin:0 auto; padding-bottom:40px; }
        .cb-header { display:flex; align-items:center; margin-bottom:24px; }
        .cb-back { background:none; border:none; color:#64748b; font-size:14px; font-weight:600; cursor:pointer; display:flex; align-items:center; gap:6px; transition:color .2s; margin-right:20px; }
        .cb-back:hover { color:#0f172a; }
        .cb-title { font-size:24px; font-weight:700; color:#0f172a; }
        
        .cb-card { background:#fff; border-radius:16px; border:1px solid #e2e8f0; box-shadow:0 4px 12px rgba(0,0,0,.03); padding:24px; margin-bottom:24px;}
        .cb-sec { font-size:14px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:1px; margin-bottom:16px; display:flex; align-items:center; gap:8px; border-bottom:1px solid #f1f5f9; padding-bottom:10px;}
        
        .cb-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(280px, 1fr)); gap:20px; margin-bottom:24px; }
        
        .cb-field { display:flex; flex-direction:column; gap:6px; position:relative; }
        .cb-field.full { grid-column:1/-1; }
        .cb-field label { font-size:13px; font-weight:600; color:#334155; }
        .cb-field input[type="text"], .cb-field input[type="number"], .cb-field select { padding:0 14px; height:48px; border-radius:10px; border:1px solid #cbd5e1; font-size:14px; font-family:'Inter',sans-serif; color:#0f172a; outline:none; transition:all .2s; background:#fff; width:100%; box-sizing:border-box;}
        .cb-field textarea { padding:14px; min-height:80px; border-radius:10px; border:1px solid #cbd5e1; font-size:14px; font-family:'Inter',sans-serif; color:#0f172a; outline:none; transition:all .2s; background:#fff; width:100%; box-sizing:border-box; resize:vertical; }
        .cb-field input:focus, .cb-field select:focus, .cb-field textarea:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
        .cb-field input:disabled { background:#f1f5f9; color:#64748b; cursor:not-allowed; }
        
        .cb-dropdown { position:absolute; top:calc(100% + 4px); left:0; right:0; background:#fff; border:1px solid #e2e8f0; border-radius:10px; box-shadow:0 4px 12px rgba(0,0,0,.1); z-index:10; max-height:200px; overflow-y:auto; }
        .cb-dd-item { padding:12px 14px; font-size:13px; cursor:pointer; border-bottom:1px solid #f1f5f9; transition:background .2s; }
        .cb-dd-item:hover { background:#f8fafc; color:#2563eb; font-weight:500; }
        
        .cb-calc-box { height:48px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:0 16px; font-size:14px; font-weight:600; color:#0f172a; display:flex; justify-content:space-between; align-items:center; box-sizing:border-box; }
        .cb-calc-lbl { color:#64748b; font-size:12px; font-weight:600; text-transform:uppercase;}
        
        .cb-foot { padding:20px 0 0; display:flex; justify-content:flex-end; gap:12px; }
        .cb-btn-save { padding:12px 24px; border-radius:10px; border:none; background:#2563eb; color:#fff; font-weight:600; font-size:15px; cursor:pointer; transition:all .2s; box-shadow:0 2px 8px rgba(37,99,235,.2);}
        .cb-btn-save:hover { background:#1d4ed8; box-shadow:0 4px 12px rgba(37,99,235,.3);}
        .cb-btn-save:disabled { opacity:.7; cursor:not-allowed; }
        
        .cb-msg { padding:12px 16px; border-radius:8px; font-size:14px; font-weight:500; margin-bottom:20px; }
        .cb-err { background:#fef2f2; color:#dc2626; border:1px solid #fecaca; }
        .cb-succ { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
      `}</style>

            <div className="cb-header">
                <button className="cb-back" onClick={() => navigate(-1)}>← Back</button>
                <h1 className="cb-title">Create New Booking</h1>
            </div>

            {msg.text && <div className={`cb-msg ${msg.type === "success" ? "cb-succ" : "cb-err"}`}>{msg.text}</div>}

            <form onSubmit={handleSubmit}>
                <div className="cb-card">
                    <div className="cb-sec">👤 Core Details</div>
                    <div className="cb-grid">
                        <div className="cb-field">
                            <label>Customer *</label>
                            <input
                                type="text"
                                placeholder="Search customer by name or phone..."
                                value={searchCustomer}
                                onChange={(e) => {
                                    setSearchCustomer(e.target.value);
                                    if(form.customer_id) setForm({ ...form, customer_id: "", customer_name: "", cust_phone: "" });
                                }}
                            />
                            {searchCustomer && !form.customer_id && (
                                <div className="cb-dropdown">
                                    {customers.filter(c => `${c.name} ${c.phone}`.toLowerCase().includes(searchCustomer.toLowerCase())).slice(0, 10).map(c => (
                                        <div key={c.id} className="cb-dd-item" onClick={() => handleCustomerSelect(c)}>
                                            {c.name} <span style={{ color: "#94a3b8" }}>({c.phone})</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="cb-field">
                            <label>Unit (Available Only) *</label>
                            <input
                                type="text"
                                placeholder="Search primary unit..."
                                value={searchUnit}
                                onChange={(e) => {
                                    setSearchUnit(e.target.value);
                                    if(form.unit_id) setForm({ ...form, unit_id: "", unit: "", project_id: "", project_name: "" });
                                }}
                            />
                            {searchUnit && !form.unit_id && (
                                <div className="cb-dropdown">
                                    {units.filter(u => u.name.toLowerCase().includes(searchUnit.toLowerCase())).slice(0, 10).map(u => (
                                        <div key={u.id} className="cb-dd-item" onClick={() => handleUnitSelect(u)}>
                                            {u.name} - {u.project}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: "#64748b", marginTop: 8 }}>
                                <input 
                                    type="checkbox" 
                                    checked={addSecondaryUnit} 
                                    onChange={(e) => {
                                        setAddSecondaryUnit(e.target.checked);
                                        if(!e.target.checked) {
                                            setForm({...form, unit_id2: "", unit2: ""});
                                            setSearchUnit2("");
                                        }
                                    }} 
                                />
                                Add a secondary unit
                            </label>

                            {addSecondaryUnit && (
                                <div style={{ marginTop: 8, position: "relative" }}>
                                    <input
                                        type="text"
                                        placeholder="Search secondary unit..."
                                        value={searchUnit2}
                                        onChange={(e) => {
                                            setSearchUnit2(e.target.value);
                                            if(form.unit_id2) setForm({ ...form, unit_id2: "", unit2: "" });
                                        }}
                                    />
                                    {searchUnit2 && !form.unit_id2 && (
                                        <div className="cb-dropdown" style={{ top: "calc(100% + 4px)" }}>
                                            {units.filter(u => u.id !== form.unit_id && u.name.toLowerCase().includes(searchUnit2.toLowerCase())).slice(0, 10).map(u => (
                                                <div key={u.id} className="cb-dd-item" onClick={() => handleUnitSelect2(u)}>
                                                    {u.name} - {u.project}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="cb-field">
                            <label>Sale Type *</label>
                            <select name="sale_type" value={form.sale_type} onChange={handleChange} required>
                                <option value="">Select Sale Type</option>
                                <option value="Singlex">Singlex</option>
                                <option value="Duplex">Duplex</option>
                                <option value="Plot">Plot</option>
                                <option value="Shop">Shop</option>
                            </select>
                        </div>
                        <div className="cb-field">
                            <label>Agreement Type *</label>
                            <select name="agreement_type" value={form.agreement_type} onChange={handleChange} required>
                                <option value="">Select Agreement Type</option>
                                {form.sale_type === "Plot" && <option value="Plot">Plot</option>}
                                {form.sale_type === "Shop" && <option value="Shop">Shop</option>}
                                {["Singlex", "Duplex"].includes(form.sale_type) && (
                                    <>
                                        <option value="Unit">Unit</option>
                                        <option value="P+C">P+C</option>
                                    </>
                                )}
                            </select>
                        </div>

                        {showPlan && (
                            <>
                                <div className="cb-field">
                                    <label>Plan Type</label>
                                    <select name="plan_type" value={form.plan_type} onChange={handleChange}>
                                        <option value="">Select Plan Type</option>
                                        <option value="Standard">Standard</option>
                                        <option value="Customized">Customized</option>
                                    </select>
                                </div>
                                <div className="cb-field">
                                    <label>Construction Type</label>
                                    <select name="construction_type" value={form.construction_type} onChange={handleChange}>
                                        <option value="">Select Construction Type</option>
                                        <option value="Open Structure">Open Structure</option>
                                        <option value="Standard Structure">Standard Structure</option>
                                    </select>
                                </div>
                            </>
                        )}
                        {showCustom && (
                            <div className="cb-field">
                                <label>Build Up Area (sq ft)</label>
                                <input type="number" name="buildarea" value={form.buildarea} onChange={handleChange} />
                            </div>
                        )}
                        <div className="cb-field full">
                            <label>Comments / Remarks</label>
                            <textarea name="comment" value={form.comment} onChange={handleChange} rows="2" />
                        </div>
                    </div>
                </div>

                <div className="cb-card">
                    <div className="cb-sec">💰 Financial Breakdown</div>
                    <div className="cb-grid">
                        <div className="cb-field">
                            <label>Actual Sale Price *</label>
                            <input type="number" name="sale_price" value={form.sale_price} onChange={handleChange} required placeholder="₹" />
                        </div>
                        <div className="cb-field">
                            <label>Land Cost *</label>
                            <input type="number" name="land_cost" value={form.land_cost} onChange={handleChange} required placeholder="₹" />
                        </div>
                        <div className="cb-field">
                            <label>Construction Cost (Auto)</label>
                            <div className="cb-calc-box">
                                <span className="cb-calc-lbl">Remaining</span>
                                <span>₹{construction_cost.toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="cb-field" style={{ justifyContent: "center" }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: "#1e40af", height: "100%", paddingLeft: 10 }}>
                                <input type="checkbox" checked={applyGST} onChange={(e) => setApplyGST(e.target.checked)} style={{ width: 18, height: 18 }} />
                                Apply GST on Construction (6%)
                            </label>
                        </div>
                    </div>

                    <div className="cb-sec" style={{ marginTop: 24 }}>💳 Source of Payment</div>
                    <div className="cb-grid">
                        <div className="cb-field">
                            <label>Payment Source *</label>
                            <select name="payment_source" value={form.payment_source} onChange={handleChange} required>
                                <option value="">Select Source</option>
                                <option value="100% Self">100% Self</option>
                                <option value="Self + Bank Loan">Self + Bank Loan</option>
                            </select>
                        </div>
                        {form.payment_source === "Self + Bank Loan" && (
                            <div className="cb-field">
                                <label>Bank Loan Amount</label>
                                <input type="number" name="loan_amount" value={form.loan_amount} onChange={handleChange} placeholder="₹" />
                            </div>
                        )}
                    </div>

                    <div className="cb-grid" style={{ marginTop: 24, gap: 12 }}>
                        <div className="cb-field">
                            <label>Self Pay Amount</label>
                            <div className="cb-calc-box" style={{ background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534" }}>
                                <span>Total expected from customer</span>
                                <span>₹{self_pay.toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="cb-field">
                            <label>Grand Total (Includes GST)</label>
                            <div className="cb-calc-box" style={{ background: "linear-gradient(135deg,#1e40af,#3b82f6)", border: "none", color: "#fff" }}>
                                <span style={{ color: "rgba(255,255,255,0.8)" }}>Total Value</span>
                                <span style={{ fontSize: 20 }}>₹{total_saleprice.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    <div className="cb-foot">
                        <button type="submit" className="cb-btn-save" disabled={saving}>
                            {saving ? "Creating..." : "✨ Complete Booking"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateBooking;
